import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui'
import { Button } from '../../components/ui'
import { CheckIcon, DocumentCheckIcon, PrinterIcon, TrashIcon, WalletIcon } from '@heroicons/react/24/outline'
import { familyTreeService, type FamilyMember } from '../../services/family-tree.service'
import { profileService } from '../../services/profile.service'
import { willService, type WillData, type WillPricing } from '../../services/will.service'
import HibahLetter, { beneficiaryName } from '../../components/will/HibahLetter'
import LoadingSpinner from '../../components/LoadingSpinner'

interface DraftRow {
  familyMemberId: string
  percentage: string
}

function memberName(m: FamilyMember): string {
  return `${m.firstName} ${m.lastName ?? ''}`.trim()
}

function priceLabel(pricing?: WillPricing): string {
  // Pricing comes from the API and differs per environment/currency — never
  // render a hardcoded fallback amount.
  if (!pricing) return '—'
  return `${pricing.displayCurrency === 'SGD' ? 'S$' : 'RM'}${Number(pricing.displayAmount).toFixed(0)}`
}

export default function MyWillPage() {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [members, setMembers] = useState<FamilyMember[]>([])
  const [draft, setDraft] = useState<DraftRow[]>([])
  const [willMeta, setWillMeta] = useState<{ unlocked: boolean; pricing?: WillPricing }>({
    unlocked: false,
  })
  const [profileInfo, setProfileInfo] = useState<{ fullName: string; nricPassport: string | null; accountId: string | null } | null>(null)
  // The letter follows the site language (BM site → BM letter) until the
  // user explicitly picks a language via the letter's own toggle.
  const letterLangTouched = useRef(false)
  const uiLetterLang = (): 'en' | 'my' =>
    i18n.language?.toLowerCase().startsWith('ms') ? 'my' : 'en'
  const [letterLang, setLetterLang] = useState<'en' | 'my'>(uiLetterLang)
  useEffect(() => {
    if (!letterLangTouched.current) setLetterLang(uiLetterLang())
  }, [i18n.language])
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [walletPaying, setWalletPaying] = useState(false)
  const [gatewayPaying, setGatewayPaying] = useState(false)

  const paywallPerks = [
    t('will.perk1', 'Print your Letter of Conditional Hibah / Wasiat'),
    t('will.perk2', 'Unlimited regeneration as your beneficiaries change'),
    t('will.perk3', 'English and Bahasa Melayu versions'),
    t('will.perk4', 'Securely stored beneficiary records'),
  ]

  const applyWill = (data: WillData) => {
    setWillMeta({ unlocked: data.unlocked, pricing: data.pricing })
    setDraft(
      data.beneficiaries.map((b) => ({
        familyMemberId: b.familyMemberId,
        percentage: String(Number(b.percentage)),
      })),
    )
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [tree, will, profile] = await Promise.all([
          familyTreeService.getTree(),
          willService.getWill(),
          profileService.getProfile(),
        ])
        setMembers(tree.members.filter((m) => !m.isDeceased))
        applyWill(will)
        setProfileInfo({
          fullName: `${profile.firstName} ${profile.lastName}`.trim(),
          nricPassport: profile.passportNumber ?? null,
          accountId: profile.memberId ?? null,
        })
      } catch (error: any) {
        console.error('Failed to load My Will:', error)
        setErrorMessage(error?.response?.data?.message || t('will.loadFailed', 'Failed to load your will'))
      } finally {
        setLoading(false)
      }
    }
    load()
    // Deliberately not keyed on t: a language switch must not re-fetch and
    // wipe in-progress beneficiary edits.
  }, [])

  // After opening the gateway payment page, poll briefly for the callback to
  // confirm payment and flip the page to unlocked.
  const startGatewayPolling = () => {
    let attempts = 0
    const timer = setInterval(async () => {
      attempts += 1
      try {
        const data = await willService.getWill()
        if (data.unlocked) {
          applyWill(data)
          setSuccessMessage(t('will.paymentConfirmed', 'Payment confirmed — your will letter is unlocked.'))
          clearInterval(timer)
        }
      } catch {
        /* keep polling */
      }
      if (attempts >= 40) clearInterval(timer)
    }, 3000)
  }

  const handlePayWallet = async () => {
    setErrorMessage('')
    setSuccessMessage('')
    setWalletPaying(true)
    try {
      const data = await willService.purchaseWithWallet()
      applyWill(data)
      setSuccessMessage(t('will.paymentSuccessful', 'Payment successful — your will letter is unlocked.'))
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || t('will.walletFailed', 'Wallet payment failed'))
    } finally {
      setWalletPaying(false)
    }
  }

  const handlePayGateway = async () => {
    setErrorMessage('')
    setSuccessMessage('')
    try {
      const { paymentUrl } = await willService.purchaseWithGateway()
      window.open(paymentUrl, '_blank')
      startGatewayPolling()
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || t('will.gatewayFailed', 'Failed to start payment'))
      setGatewayPaying(false)
    }
  }

  const handleSave = async () => {
    setErrorMessage('')
    setSuccessMessage('')
    if (!canSave) {
      setErrorMessage(t('will.invalidShares', 'Percentages must total exactly 100% with at least one beneficiary'))
      return
    }
    try {
      setSaving(true)
      const saved = await willService.updateBeneficiaries(
        draft.map((r) => ({
          familyMemberId: r.familyMemberId,
          percentage: Number(r.percentage),
        })),
      )
      applyWill(saved)
      setSuccessMessage(t('will.saveSuccess', 'Beneficiaries saved.'))
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message || t('will.saveFailed', 'Failed to save beneficiaries'))
    } finally {
      setSaving(false)
    }
  }

  const handlePrint = () => window.print()

  const selectedIds = new Set(draft.map((r) => r.familyMemberId))
  const availableMembers = members.filter((m) => !selectedIds.has(m.id))
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members])

  const total = draft.reduce((sum, r) => sum + (parseFloat(r.percentage) || 0), 0)
  const totalRounded = Math.round(total * 100) / 100
  const canSave = draft.length > 0 && totalRounded === 100

  const addRow = (familyMemberId: string) => {
    if (!familyMemberId || selectedIds.has(familyMemberId)) return
    setDraft((prev) => [...prev, { familyMemberId, percentage: '' }])
  }

  const removeRow = (familyMemberId: string) => {
    setDraft((prev) => prev.filter((r) => r.familyMemberId !== familyMemberId))
  }

  const letterBeneficiaries = draft
    .map((r) => {
      const member = memberById.get(r.familyMemberId)
      if (!member) return null
      return {
        id: r.familyMemberId,
        familyMemberId: r.familyMemberId,
        percentage: Number(r.percentage) || 0,
        createdAt: '',
        updatedAt: '',
        familyMember: {
          id: member.id,
          memberId: member.memberId,
          firstName: member.firstName,
          lastName: member.lastName,
          relationship: member.relationship,
          passportNumber: member.passportNumber,
          email: member.email,
          isDeceased: member.isDeceased,
        },
      }
    })
    .filter((b): b is NonNullable<typeof b> => b !== null)

  const unlocked = willMeta.unlocked
  const price = priceLabel(willMeta.pricing)
  const letterReady = !!profileInfo && letterBeneficiaries.length > 0

  if (loading) return <LoadingSpinner />

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="mx-auto max-w-5xl px-6 print:max-w-none print:p-0">
        <div className="mb-6 print:hidden">
          <h1 className="text-2xl font-bold text-neutral-900">{t('will.title', 'My Will')}</h1>
          <p className="mt-1 text-sm text-neutral-600">
            {t(
              'will.subtitle',
              'Designate living family members from your family tree as beneficiaries of your Akhirat account, then print your Letter of Conditional Hibah / Wasiat.',
            )}
          </p>
        </div>

        {errorMessage && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 print:hidden">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700 print:hidden">
            {successMessage}
          </div>
        )}

        <Card className="print:hidden">
          <CardHeader>
            <CardTitle>{t('will.beneficiaries', 'Beneficiaries')}</CardTitle>
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <p className="text-sm text-neutral-500">
                {t('will.noMembers', 'No living family members found. Add members in your Family Tree first.')}
              </p>
            ) : (
              <div className="space-y-3">
                {draft.length === 0 && (
                  <p className="text-sm text-neutral-500">
                    {t('will.noBeneficiaries', 'No beneficiaries selected yet. Add one below.')}
                  </p>
                )}
                {draft.map((row) => {
                  const member = memberById.get(row.familyMemberId)
                  return (
                    <div key={row.familyMemberId} className="flex items-center gap-3 rounded-lg border border-neutral-200 p-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-neutral-900">
                          {member ? memberName(member) : t('will.unknownMember', 'Unknown member')}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {member?.memberId} · {member?.relationship}
                        </p>
                      </div>
                      <div className="w-28">
                        <label className="sr-only">{t('will.percentage', 'Percentage')}</label>
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="0.01"
                            max="100"
                            step="0.01"
                            value={row.percentage}
                            onChange={(e) =>
                              setDraft((prev) =>
                                prev.map((r) =>
                                  r.familyMemberId === row.familyMemberId
                                    ? { ...r, percentage: e.target.value }
                                    : r,
                                ),
                              )
                            }
                            placeholder="%"
                            className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500"
                          />
                          <span className="ml-1 text-sm text-neutral-500">%</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeRow(row.familyMemberId)}
                        className="rounded p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                        aria-label={t('will.removeBeneficiary', 'Remove beneficiary')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )
                })}

                {availableMembers.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => addRow(e.target.value)}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:border-emerald-500 focus:ring-emerald-500"
                  >
                    <option value="">{t('will.addMember', '+ Add a family member as beneficiary…')}</option>
                    {availableMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {memberName(m)} ({m.memberId}) — {m.relationship}
                      </option>
                    ))}
                  </select>
                )}

                <div className="flex items-center justify-between border-t border-neutral-200 pt-3">
                  <span className="text-sm font-medium text-neutral-700">
                    {t('will.total', 'Total:')}{' '}
                    <span className={totalRounded === 100 ? 'text-green-700' : 'text-red-600'}>
                      {totalRounded}%
                    </span>{' '}
                    <span className="text-neutral-400">/ 100%</span>
                  </span>
                  <Button onClick={handleSave} disabled={saving || !canSave}>
                    {saving ? t('will.saving', 'Saving…') : t('will.save', 'Save Beneficiaries')}
                  </Button>
                </div>
                {!canSave && draft.length > 0 && (
                  <p className="text-xs text-neutral-500">
                    {t('will.sharesWarning', 'Shares must total exactly 100% before saving.')}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6 print:hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DocumentCheckIcon className="h-5 w-5" />
              {t('will.letterTitle', 'Letter of Conditional Hibah / Wasiat')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-xs text-neutral-500">
              {t(
                'will.letterDisclaimer',
                'The designated amount is expressed as a percentage — the RM value is whatever the account balance is when the will executes. Signatures and witnesses are completed by hand after printing. This document is generated for your convenience and is not legal advice.',
              )}
            </p>

            {unlocked && (
              <div className="mb-4 flex items-center gap-3 print:hidden">
                <div className="flex rounded-md border border-neutral-300 p-0.5">
                  {(['en', 'my'] as const).map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        letterLangTouched.current = true
                        setLetterLang(lang)
                      }}
                      className={`px-3 py-1 text-sm rounded ${
                        letterLang === lang ? 'bg-primary-600 text-white' : 'text-neutral-600'
                      }`}
                    >
                      {lang === 'en' ? 'English' : 'Bahasa Melayu'}
                    </button>
                  ))}
                </div>
                <Button onClick={handlePrint} disabled={!letterReady}>
                  <PrinterIcon className="h-4 w-4 mr-2" />
                  {t('will.print', 'Print / Save as PDF')}
                </Button>
              </div>
            )}

            {letterReady ? (
              <div className="relative">
                <div className={unlocked ? 'max-h-[600px] overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-inner' : ''}>
                  <HibahLetter
                    lang={letterLang}
                    testator={{
                      fullName: profileInfo!.fullName,
                      nricPassport: profileInfo!.nricPassport,
                      accountId: profileInfo!.accountId,
                    }}
                    beneficiaries={letterBeneficiaries}
                    preview={!unlocked}
                  />
                </div>

                {!unlocked && (
                  <>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white via-white/80 to-transparent" />
                    <div className="relative -mt-24 flex flex-col items-center py-8 text-center">
                      <span className="inline-flex items-center rounded-full bg-primary-600 px-4 py-1.5 text-sm font-medium text-white">
                        {t('will.purchase', 'Purchase The Will')}
                      </span>
                      <h2 className="mt-4 font-serif text-3xl font-semibold text-neutral-900">
                        {t('will.accessEverything', 'Access to Everything')}
                      </h2>
                      <ul className="mt-5 space-y-2">
                        {paywallPerks.map((perk) => (
                          <li key={perk} className="flex items-center justify-center gap-2 text-sm text-neutral-700">
                            <CheckIcon className="h-4 w-4 text-primary-600" />
                            {perk}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
                        <button
                          onClick={handlePayWallet}
                          disabled={walletPaying || gatewayPaying}
                          className="inline-flex items-center gap-2 rounded-full border-2 border-primary-600 px-6 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-40"
                        >
                          <WalletIcon className="h-4 w-4" />
                          {walletPaying
                            ? t('will.processing', 'Processing…')
                            : t('will.payWallet', 'Pay from Wallet — {{price}}', { price })}
                        </button>
                        <button
                          onClick={handlePayGateway}
                          disabled={walletPaying || gatewayPaying}
                          className="inline-flex items-center rounded-full bg-neutral-900 px-8 py-2.5 text-base font-semibold text-white hover:bg-neutral-800 disabled:opacity-40"
                        >
                          {gatewayPaying ? t('will.redirecting', 'Redirecting…') : price}
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-neutral-400">
                        {t('will.payHelper', 'Pay instantly from your wallet balance, or via online banking.')}
                      </p>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="p-8 text-center text-sm text-neutral-500">
                {t('will.needBeneficiary', 'Add at least one beneficiary to preview the letter.')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
